import { isProduction } from '@pypestream/utils';
import i18n, { ReadCallback } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { allLocales } from './all-locales';
import { AvailableLanguages, LanguageScopes } from './types';

const getScopeNameSpaces = (scope: LanguageScopes) => {
  switch (scope) {
    case 'pro-studio':
      return [
        `${scope}/common`,
        `${scope}/homepage`,
        `${scope}/versions`,
        `${scope}/channels`,
        `${scope}/actions`,
        `${scope}/navigation`,
        `${scope}/files`,
        `${scope}/channel-details`,
        `${scope}/workflows`,
        `${scope}/performance`,
        `${scope}/keyboard-shortcuts`,
      ];
    case 'manager':
      return [
        `${scope}/common`,
        `${scope}/projects`,
        `${scope}/preferences`,
        `${scope}/users`,
        `${scope}/teams`,
        `${scope}/logs`,
        `${scope}/super-admin`,
        `${scope}/organizations`,
      ];

    default:
      return `${scope}/common`;
  }
};
const fallbackLanguage = 'en';

const getTranslationsFromAllLocales = (
  scope: string,
  language: string,
  fileName: string
): Record<string, unknown> | null => {
  const entry = allLocales.find(
    (loc) =>
      loc.namespace === scope && loc.file === fileName && loc.lang === language
  );
  if (entry) return entry.translations as Record<string, unknown>;
  if (language !== fallbackLanguage) {
    return getTranslationsFromAllLocales(scope, fallbackLanguage, fileName);
  }
  return null;
};

const initializeI18n = (scope: LanguageScopes) => {
  i18n
    .use({
      type: 'backend',
      read: function (
        language: string,
        namespace: string,
        callback: ReadCallback
      ) {
        const [scopePart, fileName] = namespace.split('/');
        const scopeFromNs = scopePart ?? scope;
        import(`../locales/${scope}/${language}/${fileName}.json`)
          .then((obj) => {
            callback(null, obj);
          })
          .catch(() => {
            const translations = getTranslationsFromAllLocales(
              scopeFromNs,
              language,
              fileName
            );
            if (translations) {
              callback(null, translations);
            } else {
              callback(new Error(`No translations for ${namespace}`), null);
            }
          });
      },
    })
    .use(LanguageDetector)
    .use(initReactI18next)
    .init(
      {
        debug: !isProduction,
        fallbackLng: fallbackLanguage,

        // namespaces:
        // https://youtu.be/SA_9i4TtxLQ?t=314
        // https://www.i18next.com/principles/namespaces
        ns: getScopeNameSpaces(scope),
        defaultNS: `${scope}/common`,

        supportedLngs: AvailableLanguages.map((lang) => lang.code),
        interpolation: {
          escapeValue: false, // not needed for react as it escapes by default
        },
      },
      () => {
        const dir = i18n.dir();
        const lang = i18n.language;
        document.documentElement?.setAttribute('lang', lang);
        document.documentElement?.setAttribute('dir', dir);
        i18n.on('languageChanged', function (lng) {
          const newDir = i18n.dir();
          document.documentElement?.setAttribute('lang', lng);
          document.documentElement?.setAttribute('dir', newDir);
        });
      }
    );
};

const addScopeFromAllLocales = (scope: LanguageScopes) => {
  const entries = allLocales.filter((loc) => loc.namespace === scope);
  entries.forEach((entry) => {
    const ns = `${scope}/${entry.file}`;
    i18n.addResourceBundle(entry.lang, ns, entry.translations, true, true);
  });
};

const init = (scope: LanguageScopes) =>
  new Promise((resolve, reject) => {
    if (i18n.isInitialized) {
      addScopeFromAllLocales(scope);
      resolve(true);
    } else {
      initializeI18n(scope);
    }
    i18n.on('initialized', () => {
      resolve(true);
    });
    i18n.on('failedLoading', (lng: string, ns: string, msg: string) => {
      reject(`Failed loading lng - ${lng}, ns - ${ns}, msg - ${msg}`);
    });
  });

export { i18n, init, LanguageScopes, fallbackLanguage };
